import type { ProvinceDTO } from "../api/client";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

const ALL_VALUE = "__all__";

type DistrictsFilterProps = {
  provinces: ProvinceDTO[];
};

function provinceName(p: ProvinceDTO): string {
  const n = p.name as { lo?: string; th?: string; en?: string };
  return n?.lo ?? n?.th ?? n?.en ?? p.id;
}

export function DistrictsFilter({ provinces }: DistrictsFilterProps) {
  const nav = useNavigate({ from: "/app/geo/districts" });
  const search = useSearch({ from: "/app/geo/districts" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const nameLoFilter = findCondition(filters, "nameLo");
  const provinceFilter = findCondition(filters, "provinceId");
  const enabledFilter = findCondition(filters, "enabled");

  const [searchValue, setSearchValue] = useState<string>(
    (nameLoFilter?.value as string) || "",
  );
  const [provinceValue, setProvinceValue] = useState<string>(
    (provinceFilter?.value as string) || "",
  );
  const [enabledValue, setEnabledValue] = useState<string>(
    enabledFilter?.value === true
      ? "true"
      : enabledFilter?.value === false
        ? "false"
        : "",
  );

  useEffect(
    () => setSearchValue((nameLoFilter?.value as string) || ""),
    [nameLoFilter?.value],
  );
  useEffect(
    () => setProvinceValue((provinceFilter?.value as string) || ""),
    [provinceFilter?.value],
  );
  useEffect(
    () =>
      setEnabledValue(
        enabledFilter?.value === true
          ? "true"
          : enabledFilter?.value === false
            ? "false"
            : "",
      ),
    [enabledFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    let nextFilters = removeConditions(filters, "nameLo") ?? [];
    if (val) {
      nextFilters = upsertCondition(nextFilters, {
        field: "nameLo",
        op: "contains",
        value: val,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  }, 400);

  const applyProvince = (val: string) => {
    const effective = val === ALL_VALUE ? "" : val;
    setProvinceValue(effective);
    const nextFilters = effective
      ? upsertCondition(filters, {
          field: "provinceId",
          op: "eq",
          value: effective,
        })
      : removeConditions(filters, "provinceId") ?? [];
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  };

  const applyEnabled = (val: string) => {
    const effective = val === ALL_VALUE ? "" : val;
    setEnabledValue(effective);
    const nextFilters =
      effective === ""
        ? removeConditions(filters, "enabled") ?? []
        : upsertCondition(filters, {
            field: "enabled",
            op: "eq",
            value: effective === "true",
          });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  };

  const hasFilter = Boolean(
    nameLoFilter?.value || provinceFilter?.value || enabledFilter?.value,
  );

  const provinceOptions = [
    { label: "ທຸກແຂວງ", value: ALL_VALUE },
    ...provinces.map((p) => ({ label: provinceName(p), value: p.id })),
  ];

  const enabledOptions = [
    { label: "ທຸກສະຖານະ", value: ALL_VALUE },
    { label: "ເປີດ", value: "true" },
    { label: "ປິດ", value: "false" },
  ];

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-4 px-2"
      data-tourid="geo-districts-filter"
    >
      <Input
        placeholder="ຄົ້ນຫາຊື່ (ລາວ)..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="h-8 sm:max-w-2xs"
      />
      <SimpleSelect
        placeholder="ແຂວງ"
        className="h-8 w-[180px]"
        value={provinceValue || ALL_VALUE}
        onValueChange={applyProvince}
        options={provinceOptions}
      />
      <SimpleSelect
        placeholder="ສະຖານະ"
        className="h-8 w-[140px]"
        value={enabledValue || ALL_VALUE}
        onValueChange={applyEnabled}
        options={enabledOptions}
      />
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            nav({
              search: {
                ...search,
                offset: 0,
                filters: undefined,
              } as any,
            })
          }
        >
          ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
